import { z } from 'zod';

// Handoff finality per opportunity (spec §05.3, §05.21–§05.23, §08.4, §15.8; owner decisions C2/C12 of 2026-10-14).
// An opportunity is one contact + one project. LIA active → «transferencia pendiente» (LIA only coordinates) →
// «atendido por humano — definitivo» (any human reply; LIA never resumes on its own; only an admin reactivates it).

const Iso = z.string();
const Id = z.string().min(1).max(64);

export const OPPORTUNITY_CONTROL_STATES = [
  'ai_active',
  'handoff_pending',
  'human_final',
  /** «Tomar caso» (v1 §10.9): a person intervenes temporarily; resumed with a note + next step (never on its own). */
  'human_temp',
] as const;
export const OpportunityControlStateSchema = z.enum(OPPORTUNITY_CONTROL_STATES);
export type OpportunityControlState = z.infer<
  typeof OpportunityControlStateSchema
>;

export const OPPORTUNITY_CONTROL_LABELS: Record<
  OpportunityControlState,
  string
> = {
  ai_active: 'IA activa',
  handoff_pending: 'Transferencia pendiente',
  human_final: 'Atendido por humano — definitivo',
  human_temp: 'Caso tomado — intervención temporal',
};

/** Where the human reply/act that made the handoff final came from. */
export const HANDOFF_FINAL_SOURCES = [
  'inbox_reply',
  'inbox_takeover',
  'handoff_accepted',
  'whatsapp_app',
  'hubspot_inbox',
  'highlevel',
  /** Setter §08.7: a person set «Atención humana» on the opportunity in the CRM. */
  'crm_human_attention',
  /** Setter §05.22: the advisor answered the live transfer of an AI call (Telnyx Call Control, sin verificar). */
  'live_transfer_answered',
] as const;
export const HandoffFinalSourceSchema = z.enum(HANDOFF_FINAL_SOURCES);
export type HandoffFinalSource = z.infer<typeof HandoffFinalSourceSchema>;

export const HANDOFF_FINAL_SOURCE_LABELS: Record<HandoffFinalSource, string> = {
  inbox_reply: 'respondió en la bandeja de LIA',
  inbox_takeover: 'tomó la conversación en LIA',
  handoff_accepted: 'aceptó la transferencia',
  whatsapp_app: 'respondió desde la app de WhatsApp Business',
  hubspot_inbox: 'respondió desde la bandeja de HubSpot',
  highlevel: 'respondió desde HighLevel',
  crm_human_attention: 'marcó «Atención humana» en el CRM',
  live_transfer_answered: 'contestó la transferencia en vivo de la llamada',
};

/** How the person responsible for a pending handoff was chosen (spec §05.22). */
export const HANDOFF_ASSIGNMENT_SOURCES = [
  'existing_advisor',
  'round_robin',
  'manager_fallback',
  'unassigned',
] as const;
export const HandoffAssignmentSourceSchema = z.enum(HANDOFF_ASSIGNMENT_SOURCES);
export type HandoffAssignmentSource = z.infer<
  typeof HandoffAssignmentSourceSchema
>;

export const HANDOFF_ASSIGNMENT_SOURCE_LABELS: Record<
  HandoffAssignmentSource,
  string
> = {
  existing_advisor: 'Asesor ya asignado',
  round_robin: 'Asesor del proyecto (turno rotativo)',
  manager_fallback: 'Gerencia (sin asesor disponible)',
  unassigned: 'Sin responsable: nadie del proyecto puede recibirla',
};

export const PendingHandoffSchema = z.object({
  id: Id,
  assignedName: z.string().nullable(),
  assignmentSource: HandoffAssignmentSourceSchema.nullable(),
  /** SLA deadline for a person to take the case. */
  dueAt: Iso.nullable(),
  overdue: z.boolean(),
  /** Best callback time the prospect gave (LIA collects it while coordinating). */
  callbackPreference: z.string().nullable(),
});
export type PendingHandoff = z.infer<typeof PendingHandoffSchema>;

export const OpportunityControlSchema = z.object({
  projectId: Id,
  projectName: z.string(),
  state: OpportunityControlStateSchema,
  /** pending: since when; final: when the human took it. */
  since: Iso.nullable(),
  /** Who took it (final): member name, or the external sender's label (e.g. the HubSpot rep). */
  takenByName: z.string().nullable(),
  source: HandoffFinalSourceSchema.nullable(),
  pendingHandoff: PendingHandoffSchema.nullable(),
  /** Last admin reactivation (audited). */
  reactivation: z.object({ at: Iso, reason: z.string() }).nullable(),
});
export type OpportunityControl = z.infer<typeof OpportunityControlSchema>;

/** «Reactivar LIA» — workspace admins only, reason required (owner decision C2). */
export const ReactivateAiSchema = z.object({
  expectedVersion: z.number().int().optional(),
  reason: z.string().trim().min(5).max(500),
  /** Opportunity to reactivate; defaults to the conversation's active project (or every handed-off one when none). */
  projectId: Id.optional(),
});
export type ReactivateAi = z.infer<typeof ReactivateAiSchema>;

/**
 * Funnel meaning written to the CRM on a final handoff (spec §05.23 «Atención humana»). CRM stage mappings map this key
 * to the portal's value (Integraciones → CRM → etapas, outbound).
 */
export const HUMAN_ATTENTION_STAGE = 'human_attention';
